import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer } from '@react-navigation/native';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import AppNavigator from './src/navigation';
import { useAuthStore } from './src/stores/authStore';
import { supabase } from './src/api/supabase';
import { runVehicleAudit } from './src/utils/vehicleAssignmentAuditor';
import { startVehicleMonitoring } from './src/utils/vehicleAssignmentMonitor';
import { FeatureFlagProvider } from './src/features/FeatureFlagProvider';
import { OfflineManager } from './src/services/offline/OfflineManager';
import { initializeWarningSuppressions } from './src/utils/warningSuppressions';
import { initializeWebFixes } from './src/utils/webCompatibility';

// Import print styles for web
import './src/styles/print.css';

// Linking configuration for web URL persistence
const linking = {
  prefixes: ['http://localhost:8081', 'exp://'],
  config: {
    screens: {
      Main: {
        screens: {
          Dashboard: 'dashboard',
          Vehicles: 'vehicles',
          Drivers: 'drivers',
          Assignments: 'assignments',
          AssignmentHistory: 'assignment-history',
          Analytics: 'analytics',
          Accounting: 'accounting',
          ImportExport: 'import-export',
          Maintenance: 'maintenance',
          LiveMap: 'live-map',
          Settings: 'settings',
        },
      },
      Auth: {
        screens: {
          Login: 'login',
        },
      },
    },
  },
};

// Create a client for React Query with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - garbage collection time
      refetchOnWindowFocus: false, // Don't refetch on window focus (mobile friendly)
      refetchOnReconnect: true, // Refetch when coming back online
      refetchOnMount: false, // Don't refetch if data is fresh
    },
    mutations: {
      retry: 1,
    },
  },
});

export default function App() {
  const [isReady, setIsReady] = useState(false);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    // Initialize warning suppressions for cleaner console output
    initializeWarningSuppressions();
    
    // Initialize web-specific compatibility fixes
    initializeWebFixes();
    
    // Initialize app services
    const initializeApp = async () => {
      try {
        // Initialize offline services first
        console.log('🚀 Initializing offline services...');
        const offlineManager = OfflineManager.getInstance();
        await offlineManager.initialize();
        console.log('✅ Offline services initialized successfully');

        // Initialize vehicle status monitoring
        import('./src/services/VehicleStatusMonitor').then(() => {
          console.log('🔄 Vehicle status monitoring initialized');
        });

        // Run automatic vehicle assignment audit (if user is logged in)
        // The authStore handles session restoration from localStorage
        if (user) {
          try {
            console.log('🔍 Running automatic vehicle assignment audit...');
            const auditResult = await runVehicleAudit();
            if (auditResult.fixedCount > 0) {
              console.log(`✅ Auto-audit: Fixed ${auditResult.fixedCount} vehicle assignment inconsistencies`);
            } else {
              console.log('✅ Auto-audit: All vehicle assignments are consistent');
            }
            
            // Start periodic monitoring (every 2 hours)
            startVehicleMonitoring(120);
            console.log('🔄 Started automatic vehicle assignment monitoring');
            
          } catch (auditError) {
            console.warn('⚠️ Vehicle audit failed:', auditError);
            // Don't block app initialization if audit fails
          }
        }
      } catch (error) {
        console.error('Error initializing app:', error);
      } finally {
        setIsReady(true);
      }
    };
    
    initializeApp();
  }, [user]);

  if (!isReady) {
    // Return loading screen
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagProvider>
        <SafeAreaProvider>
          <NavigationContainer linking={linking}>
            <AppNavigator />
            <StatusBar style="auto" />
          </NavigationContainer>
        </SafeAreaProvider>
      </FeatureFlagProvider>
    </QueryClientProvider>
  );
}
